const VERSION = '0.2.0';

function copyPlainObject(source) {
  if (!source) {
    return {};
  }
  const target = {};
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      target[key] = source[key];
    }
  }
  return target;
}

function slugify(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function isFunction(value) {
  return typeof value === 'function';
}

function isString(value) {
  return typeof value === 'string';
}

function resolveFactory(Lref, descriptor) {
  if (!descriptor) {
    return null;
  }
  if (isFunction(descriptor)) {
    return descriptor;
  }
  if (isString(descriptor)) {
    let scope = Lref;
    const parts = descriptor.split('.');
    for (let i = 0; i < parts.length; i += 1) {
      if (!scope) {
        return null;
      }
      scope = scope[parts[i]];
    }
    return isFunction(scope) ? scope : null;
  }
  return null;
}

function now() {
  if (typeof performance !== 'undefined' && isFunction(performance.now)) {
    return performance.now();
  }
  return Date.now();
}

const DEFAULT_OPTIONS = {
  position: 'topright',
  collapsed: true,
  autoActivate: true,
  emptyMessage: 'No tile layers available',
  previewSize: 48,
  layers: [],
  startActiveId: null
};

function defineTileLayerManagerControl(L) {
  if (!L || !L.Control) {
    throw new Error('A Leaflet instance is required to define the TileLayerManager control.');
  }

  if (L.Control.TileLayerManager) {
    return L.Control.TileLayerManager;
  }

  const TileLayerManagerControl = L.Control.extend({
    options: DEFAULT_OPTIONS,

    initialize(layers, options) {
      let incomingLayers = [];
      if (Array.isArray(layers)) {
        incomingLayers = layers.slice();
      } else if (layers && typeof layers === 'object') {
        options = layers;
      }

      L.Util.setOptions(this, options || {});

      if (Array.isArray(this.options.layers) && this.options.layers.length) {
        incomingLayers = incomingLayers.concat(this.options.layers);
      }

      this._L = L;
      this._layers = [];
      this._map = null;
      this._container = null;
      this._listEl = null;
      this._currentBtn = null;
      this._expanded = false;
      this._activeId = null;
      this._pendingActiveId = null;
      this._toggleHandler = null;
      this._listClickHandler = null;
      this._outsideHandler = null;
      this._currentKeyHandler = null;
      this._listKeyHandler = null;
      this._mouseLeaveHandler = null;

      for (let i = 0; i < incomingLayers.length; i += 1) {
        this._layers.push(this._normalizeConfig(incomingLayers[i]));
      }
    },

    onAdd(map) {
      this._map = map;

      const container = L.DomUtil.create('div', 'leaflet-control leaflet-tilelayer-manager');
      container.setAttribute('aria-expanded', this._expanded ? 'true' : 'false');

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      this._currentBtn = L.DomUtil.create('button', 'ltlm-current', container);
      this._currentBtn.type = 'button';
      this._currentBtn.setAttribute('aria-haspopup', 'listbox');
      this._currentBtn.setAttribute('aria-expanded', this._expanded ? 'true' : 'false');

      this._listEl = L.DomUtil.create('div', 'ltlm-list', container);
      this._listEl.setAttribute('role', 'listbox');
      this._listEl.setAttribute('aria-label', 'Available tile layers');
      this._listEl.tabIndex = -1;

      this._toggleHandler = this.toggle.bind(this);
      this._currentBtn.addEventListener('click', this._toggleHandler);

      this._currentKeyHandler = this._handleCurrentKeydown.bind(this);
      this._currentBtn.addEventListener('keydown', this._currentKeyHandler);

      this._listClickHandler = this._handleListClick.bind(this);
      this._listEl.addEventListener('click', this._listClickHandler);

      this._listKeyHandler = this._handleListKeydown.bind(this);
      this._listEl.addEventListener('keydown', this._listKeyHandler);

      this._mouseLeaveHandler = this._handleMouseLeave.bind(this);
      container.addEventListener('mouseleave', this._mouseLeaveHandler);

      this._container = container;

      this._render();

      let startId = this._pendingActiveId || this._activeId || this.options.startActiveId;
      if (!startId && this._layers.length && this.options.autoActivate) {
        startId = this._layers[0].id;
      }

      if (startId) {
        this.activate(startId, { silent: true });
        this._pendingActiveId = null;
      } else {
        this._render();
      }

      if (this.options.collapsed) {
        this.close();
      } else {
        this.open();
      }

      return container;
    },

    onRemove(map) {
      this._detachDomEvents();

      const mapRef = map || this._map;
      if (mapRef && this._activeId) {
        const active = this._getEntry(this._activeId);
        if (active && active.layer && mapRef.hasLayer(active.layer)) {
          mapRef.removeLayer(active.layer);
        }
      }

      this._container = null;
      this._listEl = null;
      this._currentBtn = null;

      return this;
    },

    toggle() {
      if (this._expanded) {
        this.close();
      } else {
        this.open();
      }
      return this;
    },

    open() {
      if (this._expanded) {
        return this;
      }
      this._expanded = true;
      if (this._container) {
        this._container.classList.add('is-expanded');
        this._container.classList.remove('is-collapsed');
        this._container.setAttribute('aria-expanded', 'true');
      }
      if (this._currentBtn) {
        this._currentBtn.setAttribute('aria-expanded', 'true');
      }
      if (this._listEl) {
        this._listEl.setAttribute('aria-hidden', 'false');
      }
      this._bindOutsideHandler();
      return this;
    },

    close() {
      this._expanded = false;
      if (this._container) {
        this._container.classList.remove('is-expanded');
        this._container.classList.add('is-collapsed');
        this._container.setAttribute('aria-expanded', 'false');
      }
      if (this._currentBtn) {
        this._currentBtn.setAttribute('aria-expanded', 'false');
      }
      if (this._listEl) {
        this._listEl.setAttribute('aria-hidden', 'true');
      }
      this._unbindOutsideHandler();
      return this;
    },

    addTileLayer(config, options) {
      const entry = this._normalizeConfig(config);
      const existingIndex = this._findEntryIndex(entry.id);
      if (existingIndex !== -1) {
        this.removeTileLayer(entry.id);
      }
      this._layers.push(entry);
      this._render();

      const activateOption = options && Object.prototype.hasOwnProperty.call(options, 'activate')
        ? options.activate
        : null;
      const shouldActivate = activateOption !== null
        ? activateOption
        : (this.options.autoActivate && this._layers.length === 1);
      if (shouldActivate) {
        this.activate(entry.id, { silent: options && options.silent });
      } else if (this._map) {
        this._map.fire('tilelayermanager:layeradded', {
          id: entry.id,
          name: entry.name,
          metadata: entry.metadata
        });
      }
      return this;
    },

    removeTileLayer(id) {
      const index = this._findEntryIndex(id);
      if (index === -1) {
        return this;
      }

      const entry = this._layers[index];

      if (entry.layer && this._map && this._map.hasLayer(entry.layer)) {
        this._map.removeLayer(entry.layer);
      }

      this._layers.splice(index, 1);

      if (this._map) {
        this._map.fire('tilelayermanager:layerremoved', {
          id: entry.id,
          name: entry.name,
          metadata: entry.metadata
        });
      }

      if (this._activeId === id) {
        this._activeId = null;
        if (this._layers.length && this.options.autoActivate) {
          this.activate(this._layers[0].id);
        } else {
          this._render();
        }
      } else {
        this._render();
      }

      return this;
    },

    activate(id, options) {
      const entry = this._getEntry(id);
      if (!entry) {
        return this;
      }

      const previous = this._activeId ? this._getEntry(this._activeId) : null;
      this._bringToFront(entry.id);

      if (!this._map) {
        this._activeId = entry.id;
        this._pendingActiveId = entry.id;
        this._render();
        return this;
      }

      const layer = this._ensureLayer(entry);

      if (previous && previous.layer && previous.layer !== layer && this._map.hasLayer(previous.layer)) {
        this._map.removeLayer(previous.layer);
      }

      if (!this._map.hasLayer(layer)) {
        this._map.addLayer(layer);
      }

      this._activeId = entry.id;
      entry.lastActivated = now();
      this._render();

      if (!options || options.silent !== true) {
        this._map.fire('tilelayermanager:change', {
          id: entry.id,
          name: entry.name,
          layer,
          metadata: entry.metadata
        });
      }

      this.close();

      return this;
    },

    getActiveLayerId() {
      return this._activeId;
    },

    getActiveLayer() {
      const entry = this._getEntry(this._activeId);
      if (!entry) {
        return null;
      }
      return {
        id: entry.id,
        name: entry.name,
        layer: entry.layer || null,
        metadata: entry.metadata
      };
    },

    getTileLayer(id) {
      const entry = this._getEntry(id);
      if (!entry) {
        return null;
      }
      return {
        id: entry.id,
        name: entry.name,
        layer: entry.layer || null,
        metadata: entry.metadata
      };
    },

    hasTileLayer(id) {
      return this._findEntryIndex(id) !== -1;
    },

    listTileLayers() {
      const output = [];
      for (let i = 0; i < this._layers.length; i += 1) {
        const entry = this._layers[i];
        output.push({
          id: entry.id,
          name: entry.name,
          preview: entry.preview,
          url: entry.url,
          metadata: entry.metadata,
          lastActivated: entry.lastActivated
        });
      }
      return output;
    },

    _normalizeConfig(config) {
      if (!config) {
        throw new Error('Tile layer configuration is required.');
      }

      let id = config.id || config.key || config.layerId || null;
      if (!id && config.name) {
        id = slugify(config.name);
      }
      if (!id) {
        throw new Error('Tile layer configuration must include an id or a name.');
      }

      const normalized = {
        id,
        name: config.name || id,
        preview: config.preview || config.previewUrl || config.exampleImage || config.image || null,
        url: config.url || config.tileUrl || config.template || null,
        options: copyPlainObject(config.options || config.layerOptions || {}),
        factory: config.factory || config.layerFactory || config.type || null,
        createLayer: config.createLayer || config.layerBuilder || null,
        layer: config.layer || config.instance || null,
        metadata: config.metadata || config.meta || {},
        original: config,
        lastActivated: typeof config.lastActivated === 'number' ? config.lastActivated : 0
      };

      if (!normalized.factory && normalized.url && !normalized.createLayer && !normalized.layer) {
        normalized.factory = 'tileLayer';
      }

      return normalized;
    },

    _ensureLayer(entry) {
      if (entry.layer && isFunction(entry.layer.addTo)) {
        return entry.layer;
      }

      if (entry.createLayer) {
        const layerFromFactory = entry.createLayer({
          L: this._L,
          map: this._map,
          metadata: entry.metadata,
          options: copyPlainObject(entry.options),
          url: entry.url
        });

        if (!layerFromFactory || !isFunction(layerFromFactory.addTo)) {
          throw new Error('createLayer must return a Leaflet layer instance.');
        }

        entry.layer = layerFromFactory;
        return entry.layer;
      }

      const factoryFn = resolveFactory(this._L, entry.factory || 'tileLayer');
      if (!factoryFn) {
        throw new Error(`Unable to resolve factory "${entry.factory || 'tileLayer'}".`);
      }

      entry.layer = factoryFn.call(this._L, entry.url, copyPlainObject(entry.options));
      return entry.layer;
    },

    _bringToFront(id) {
      const index = this._findEntryIndex(id);
      if (index <= 0) {
        return;
      }
      const entry = this._layers.splice(index, 1)[0];
      this._layers.unshift(entry);
    },

    _getEntry(id) {
      if (!id) {
        return null;
      }
      for (let i = 0; i < this._layers.length; i += 1) {
        if (this._layers[i].id === id) {
          return this._layers[i];
        }
      }
      return null;
    },

    _findEntryIndex(id) {
      for (let i = 0; i < this._layers.length; i += 1) {
        if (this._layers[i].id === id) {
          return i;
        }
      }
      return -1;
    },

    _render() {
      if (!this._container || !this._currentBtn || !this._listEl) {
        return;
      }

      const fragment = document.createDocumentFragment();
      const activeEntry = this._getEntry(this._activeId);
      const displayEntry = activeEntry || (this._layers.length ? this._layers[0] : null);

      if (this._layers.length === 0) {
        this._currentBtn.disabled = true;
        this._setButtonContent(this._currentBtn, null, this.options.emptyMessage);
        this._listEl.innerHTML = '';
        this._listEl.setAttribute('aria-disabled', 'true');
        return;
      }

      this._currentBtn.disabled = false;
      this._listEl.removeAttribute('aria-disabled');

      for (let i = 0; i < this._layers.length; i += 1) {
        const entry = this._layers[i];
        if (activeEntry && entry.id === activeEntry.id) {
          continue;
        }
        const item = this._createListItem(entry);
        fragment.appendChild(item);
      }

      this._listEl.innerHTML = '';
      this._listEl.appendChild(fragment);
      if (!this._listEl.childElementCount) {
        this._listEl.setAttribute('aria-disabled', 'true');
      }

      this._setButtonContent(this._currentBtn, displayEntry, displayEntry ? displayEntry.name : 'Select tile layer');
      if (displayEntry) {
        this._currentBtn.setAttribute('data-layer-id', displayEntry.id);
      } else {
        this._currentBtn.removeAttribute('data-layer-id');
      }

      if (this._listEl) {
        this._listEl.setAttribute('aria-hidden', this._expanded ? 'false' : 'true');
      }
    },

    _createListItem(entry) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ltlm__entry';
      item.setAttribute('role', 'option');
      item.setAttribute('data-layer-id', entry.id);

      if (entry.id === this._activeId) {
        item.classList.add('is-active');
        item.setAttribute('aria-selected', 'true');
      } else {
        item.setAttribute('aria-selected', 'false');
      }

      this._setButtonContent(item, entry, entry.name);

      return item;
    },

    _setButtonContent(button, entry, fallbackText) {
      while (button.firstChild) {
        button.removeChild(button.firstChild);
      }

      if (entry && entry.preview) {
        const preview = document.createElement('img');
        preview.src = entry.preview;
        preview.alt = '';
        preview.className = 'ltlm-preview';
        preview.setAttribute('aria-hidden', 'true');
        button.appendChild(preview);
      }

      const label = document.createElement('span');
      label.className = 'ltlm-name';
      label.textContent = entry ? entry.name : fallbackText;
      button.appendChild(label);
      button.title = entry ? entry.name : fallbackText;
    },

    _handleListClick(event) {
      let target = event.target;
      while (target && target !== this._listEl) {
        if (target.dataset && target.dataset.layerId) {
          this.activate(target.dataset.layerId);
          this.close();
          break;
        }
        target = target.parentNode;
      }
    },

    _handleCurrentKeydown(event) {
      const key = event.key;
      if (key === 'ArrowDown' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.open();
        this._focusListItem(0);
      }
    },

    _handleListKeydown(event) {
      const key = event.key;
      if (key === 'Escape') {
        event.preventDefault();
        this.close();
        if (this._currentBtn) {
          this._currentBtn.focus();
        }
        return;
      }

      const items = this._listEl.querySelectorAll('.ltlm__entry');
      if (!items.length) {
        return;
      }

      let currentIndex = -1;
      for (let i = 0; i < items.length; i += 1) {
        if (items[i] === document.activeElement) {
          currentIndex = i;
          break;
        }
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        let nextIndex = currentIndex + 1;
        if (nextIndex >= items.length) {
          nextIndex = 0;
        }
        this._focusListItem(nextIndex);
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        let prevIndex = currentIndex - 1;
        if (prevIndex < 0) {
          prevIndex = items.length - 1;
        }
        this._focusListItem(prevIndex);
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (currentIndex >= 0 && items[currentIndex]) {
          const layerId = items[currentIndex].dataset.layerId;
          this.activate(layerId);
          this.close();
        }
      }
    },

    _focusListItem(index) {
      const items = this._listEl.querySelectorAll('.ltlm__entry');
      if (!items.length) {
        return;
      }
      const safeIndex = Math.min(Math.max(index, 0), items.length - 1);
      const item = items[safeIndex];
      if (item) {
        item.focus();
      }
    },

    _handleMouseLeave() {
      if (this._expanded) {
        this.close();
      }
    },

    _bindOutsideHandler() {
      if (this._outsideHandler) {
        return;
      }
      this._outsideHandler = (event) => {
        if (!this._container) {
          return;
        }
        if (!this._container.contains(event.target)) {
          this.close();
        }
      };
      document.addEventListener('mousedown', this._outsideHandler, true);
      document.addEventListener('touchstart', this._outsideHandler, true);
    },

    _unbindOutsideHandler() {
      if (!this._outsideHandler) {
        return;
      }
      document.removeEventListener('mousedown', this._outsideHandler, true);
      document.removeEventListener('touchstart', this._outsideHandler, true);
      this._outsideHandler = null;
    },

    _detachDomEvents() {
      if (this._currentBtn && this._toggleHandler) {
        this._currentBtn.removeEventListener('click', this._toggleHandler);
      }
      if (this._currentBtn && this._currentKeyHandler) {
        this._currentBtn.removeEventListener('keydown', this._currentKeyHandler);
      }
      if (this._listEl && this._listClickHandler) {
        this._listEl.removeEventListener('click', this._listClickHandler);
      }
      if (this._listEl && this._listKeyHandler) {
        this._listEl.removeEventListener('keydown', this._listKeyHandler);
      }
      if (this._container && this._mouseLeaveHandler) {
        this._container.removeEventListener('mouseleave', this._mouseLeaveHandler);
      }
      this._unbindOutsideHandler();
      this._toggleHandler = null;
      this._listClickHandler = null;
      this._currentKeyHandler = null;
      this._listKeyHandler = null;
      this._mouseLeaveHandler = null;
    }
  });

  TileLayerManagerControl.VERSION = VERSION;

  L.Control.TileLayerManager = TileLayerManagerControl;
  if (!L.tileLayerManager) {
    L.tileLayerManager = function tileLayerManagerFactory(layers, options) {
      return new TileLayerManagerControl(layers, options);
    };
  }

  return TileLayerManagerControl;
}

export { defineTileLayerManagerControl, VERSION };
